import { useState, useRef } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiUpload } from "../lib/api";
import { COLORS } from "../lib/colors";

export default function BulkUpload({ uploadPath, entityId, extraParams = {}, onComplete }) {
  const { session } = useAuth();
  const token = session?.access_token;
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [duplicateMode, setDuplicateMode] = useState("skip");
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState(null);

  const handleFiles = (e) => {
    setFiles(Array.from(e.target.files));
    setResults(null);
  };

  const totalSizeMB = (files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(1);

  const processAll = async () => {
    setProcessing(true);
    setProgress({ current: 0, total: files.length });
    const res = { success: 0, failed: 0, skipped: 0, details: [] };

    for (let i = 0; i < files.length; i++) {
      setProgress({ current: i + 1, total: files.length });
      const file = files[i];
      const formData = new FormData();
      formData.append("file", file);

      // Add extra params as query string
      const params = new URLSearchParams({ overwrite: duplicateMode === "overwrite", ...extraParams });
      const path = `/upload/${uploadPath}/${entityId}?${params}`;

      try {
        const data = await apiUpload(path, formData, token);
        if (data.skipped) {
          res.skipped++;
          res.details.push({ filename: file.name, status: "skipped", message: "Duplicate" });
        } else {
          res.success++;
          res.details.push({ filename: file.name, status: "success", score: data.efmd_score });
        }
      } catch (err) {
        res.failed++;
        res.details.push({ filename: file.name, status: "failed", message: err.message });
      }
    }

    setResults(res);
    setProcessing(false);
    if (onComplete) onComplete();
  };

  const clearResults = () => {
    setResults(null);
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div>
      {/* Duplicate handling */}
      <div style={{ marginBottom: 16, display: "flex", gap: 16, alignItems: "center" }}>
        <span style={{ fontSize: 13, color: COLORS.textSecondary }}>Duplicates:</span>
        {["skip", "overwrite"].map((mode) => (
          <label key={mode} style={{ fontSize: 13, color: COLORS.text, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
            <input type="radio" name="dupMode" checked={duplicateMode === mode} onChange={() => setDuplicateMode(mode)} />
            {mode === "skip" ? "Skip" : "Overwrite"}
          </label>
        ))}
      </div>

      {/* File input */}
      <div style={{
        border: `2px dashed ${COLORS.border}`, borderRadius: 12, padding: 32,
        textAlign: "center", marginBottom: 16, cursor: "pointer",
        backgroundColor: COLORS.surfaceAlt,
      }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx"
          onChange={handleFiles}
          style={{ display: "none" }}
        />
        <div style={{ fontSize: 28, marginBottom: 8 }}>&#x1F4C1;</div>
        <p style={{ fontSize: 14, color: COLORS.textSecondary }}>
          Drag & drop CV files here, or click to browse
        </p>
        <p style={{ fontSize: 12, color: COLORS.textMuted }}>PDF or DOCX</p>
      </div>

      {files.length > 0 && !results && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 12 }}>
            {files.length} file{files.length > 1 ? "s" : ""} selected ({totalSizeMB} MB)
          </p>

          {processing && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 4 }}>
                Processing {progress.current} of {progress.total}...
              </div>
              <div style={{ height: 6, backgroundColor: COLORS.surfaceAlt, borderRadius: 3 }}>
                <div style={{
                  height: "100%", borderRadius: 3, backgroundColor: COLORS.accent,
                  width: `${(progress.current / progress.total) * 100}%`,
                  transition: "width 0.3s ease",
                }} />
              </div>
            </div>
          )}

          <button
            onClick={processAll}
            disabled={processing}
            style={{
              padding: "10px 24px", fontSize: 14, fontWeight: 600,
              backgroundColor: COLORS.accent, color: "#fff",
              border: "none", borderRadius: 8,
              cursor: processing ? "not-allowed" : "pointer",
              opacity: processing ? 0.7 : 1,
            }}
          >{processing ? "Processing..." : "Process All CVs"}</button>
        </div>
      )}

      {results && (
        <div style={{
          backgroundColor: COLORS.surface, borderRadius: 12,
          border: `1px solid ${COLORS.border}`, padding: 20,
        }}>
          <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
            <span style={{ fontSize: 14, color: COLORS.green, fontWeight: 600 }}>
              {results.success} successful
            </span>
            {results.failed > 0 && (
              <span style={{ fontSize: 14, color: COLORS.red, fontWeight: 600 }}>
                {results.failed} failed
              </span>
            )}
            {results.skipped > 0 && (
              <span style={{ fontSize: 14, color: COLORS.textMuted, fontWeight: 600 }}>
                {results.skipped} skipped
              </span>
            )}
          </div>

          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {results.details.map((d, i) => (
              <div key={i} style={{
                padding: "6px 0", borderBottom: `1px solid ${COLORS.borderLight}`,
                fontSize: 13, display: "flex", justifyContent: "space-between",
              }}>
                <span style={{ color: COLORS.text }}>{d.filename}</span>
                <span style={{
                  color: d.status === "success" ? COLORS.green : d.status === "failed" ? COLORS.red : COLORS.textMuted,
                }}>
                  {d.status === "success" ? `Score: ${d.score}/100` : d.message}
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={clearResults}
            style={{
              marginTop: 12, padding: "8px 16px", fontSize: 13,
              backgroundColor: COLORS.surfaceAlt, color: COLORS.textSecondary,
              border: `1px solid ${COLORS.border}`, borderRadius: 6, cursor: "pointer",
            }}
          >Clear Results</button>
        </div>
      )}
    </div>
  );
}
